/**
 * Сцена скина игрока: персонаж на фоне среза киви (кабинет, публичный профиль) или без
 * фона и со своей 2D-картинкой — камео SPY_me в архипелаге лендинга.
 *
 * Сначала показывается готовый 2D-рендер (mc-heads) — он лёгкий и виден сразу.
 * На компьютере, когда страница загрузилась и браузер простаивает, подгружается
 * skinview3d (Three.js, ≈99 КБ в brotli), и 3D-модель плавно сменяет картинку: её
 * можно вращать, она «дышит», а при появлении машет рукой. На телефонах и слабых
 * устройствах 3D включается кнопкой «Покрутить в 3D» — разбор Three.js там занимает
 * процессор заметно дольше. 3D не загружается вовсе при «уменьшить движение»,
 * в режиме экономии трафика или без WebGL — остаётся 2D.
 * Пока сцена не видна (прокручена или вкладка скрыта), отрисовка стоит на паузе.
 */
import { el, svg, icon, toast, onLeave, reducedMotion } from "./ui.js";

const SKINVIEW_URL = "/js/vendor/skinview3d.js?v=3.4.2";

let skinviewPromise = null;

/** Загрузить skinview3d один раз на страницу (UMD-сборка кладёт window.skinview3d). */
function loadSkinview() {
  if (window.skinview3d) return Promise.resolve(window.skinview3d);
  if (!skinviewPromise) {
    skinviewPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = SKINVIEW_URL;
      script.async = true;
      script.onload = () => resolve(window.skinview3d);
      script.onerror = () => {
        skinviewPromise = null;
        reject(new Error("Не удалось загрузить skinview3d"));
      };
      document.head.appendChild(script);
    });
  }
  return skinviewPromise;
}

/**
 * Текстура скина (PNG 64×64) для 3D. По hash — прямо с сервера текстур Mojang
 * (отдаёт CORS-заголовки), без hash — mc-heads по нику.
 */
export function skinTextureUrl(hash, name) {
  return hash
    ? `https://textures.minecraft.net/texture/${hash}`
    : `https://mc-heads.net/skin/${encodeURIComponent(name)}`;
}

/** Готовый 2D-рендер персонажа во весь рост (mc-heads) — мгновенная заглушка и запасной вид. */
function bodyRenderUrl(hash, name) {
  return `https://mc-heads.net/body/${hash || encodeURIComponent(name)}/right`;
}

/** Можно ли показывать 3D: без «меньше движения» и без режима экономии трафика. */
function allow3d() {
  if (reducedMotion()) return false;
  const connection = navigator.connection;
  return !(connection && connection.saveData);
}

/**
 * Загружать ли 3D сразу. На телефонах и слабых устройствах разбор Three.js занимает
 * процессор на сотни миллисекунд — там 3D включается кнопкой «Покрутить в 3D».
 */
function autoStart3d() {
  if (window.matchMedia("(pointer: coarse)").matches) return false;
  const memory = navigator.deviceMemory;
  return !(memory && memory <= 4);
}

/** Выполнить fn, когда страница загрузилась и браузер простаивает. */
function whenIdle(fn) {
  const schedule = () => (window.requestIdleCallback ? window.requestIdleCallback(fn, { timeout: 3000 }) : setTimeout(fn, 300));
  if (document.readyState === "complete") schedule();
  else window.addEventListener("load", schedule, { once: true });
}

/**
 * Срез киви за персонажем — мотив лендинга: кожура, мякоть с волокнами,
 * светлая сердцевина и тёмные семечки по кругу. Статичный, чисто декоративный.
 */
function kiwiSlice() {
  const seeds = [];
  const fibers = [];
  for (let index = 0; index < 18; index++) {
    const angle = (index / 18) * 360;
    seeds.push(svg("ellipse", {
      cx: 100, cy: 64, rx: 2.6, ry: 6,
      transform: `rotate(${angle} 100 100)`,
      fill: "#1b1a14", opacity: 0.85,
    }));
  }
  for (let index = 0; index < 36; index++) {
    const angle = (index / 36) * 360;
    fibers.push(svg("line", {
      x1: 100, y1: 76, x2: 100, y2: 14,
      transform: `rotate(${angle + 5} 100 100)`,
      stroke: "#e8f5e0", "stroke-width": 0.8, opacity: 0.22,
    }));
  }
  return svg("svg", { viewBox: "0 0 200 200", class: "jk-stage__kiwi", "aria-hidden": "true" }, [
    svg("defs", null, [
      svg("radialGradient", { id: "jk-kiwi-flesh", cx: "50%", cy: "50%", r: "50%" }, [
        svg("stop", { offset: "0%", "stop-color": "#dcedc8" }),
        svg("stop", { offset: "38%", "stop-color": "#9ccc65" }),
        svg("stop", { offset: "100%", "stop-color": "#4caf50" }),
      ]),
    ]),
    svg("circle", { cx: 100, cy: 100, r: 99, fill: "#6d4c2a" }),
    svg("circle", { cx: 100, cy: 100, r: 91, fill: "url(#jk-kiwi-flesh)" }),
    svg("g", null, fibers),
    svg("g", null, seeds),
    svg("ellipse", { cx: 100, cy: 100, rx: 20, ry: 23, fill: "#f1f8e9", opacity: 0.9 }),
  ]);
}

/**
 * Создать сцену скина.
 *
 * @param {object} options
 * @param {string|null} options.hash  hash текстуры (из кэша сайта) или null
 * @param {string} options.name        ник — для запасного рендера по нику
 * @param {boolean} options.greet      помахать рукой, когда 3D-модель появится
 * @param {boolean} options.hint       подпись «Потяните, чтобы повернуть» под сценой
 * @param {boolean} options.backdrop   срез киви за персонажем
 * @param {string|null} options.fallbackSrc  своя 2D-картинка вместо рендера mc-heads
 *        (камео на лендинге); при смене скина она не меняется
 * @param {boolean} options.touchLaunch  кнопка «Покрутить в 3D» на телефонах; без неё там остаётся 2D
 * @param {{zoom?: number, offsetY?: number, yaw?: number}} options.view  кадр 3D: приближение,
 *        сдвиг модели по высоте (отрицательный — кадр выше, по пояс) и поворот в радианах
 * @returns {{ node: HTMLElement, setSkin: (hash: string|null) => void, wave: () => void,
 *             previewFile: (url: string, slim: boolean) => Promise<boolean> }}
 */
export function skinStage({
  hash = null,
  name = "",
  greet = false,
  hint = true,
  backdrop = true,
  fallbackSrc = null,
  touchLaunch = true,
  view = {},
} = {}) {
  const fallback = el("img", {
    src: fallbackSrc || bodyRenderUrl(hash, name),
    alt: name ? `Скин игрока ${name}` : "Скин игрока",
    decoding: "async",
    draggable: "false",
  });
  const model = el("div", { class: "jk-stage__model" }, fallback);
  const hintNode = hint
    ? el("p", { class: "absolute -bottom-2 inset-x-0 flex items-center justify-center gap-1.5 text-xs text-muted opacity-0 transition-opacity duration-300" },
      [icon("rotate", 13), "Потяните, чтобы повернуть"])
    : null;
  const node = el("div", { class: "jk-stage" }, [backdrop ? kiwiSlice() : null, model, hintNode]);

  let viewer = null;
  let skinview = null;
  let destroyed = false;
  let visible = true;
  let current = { hash, name };
  // Файл для предпросмотра, выбранный до того, как 3D успело загрузиться.
  let pendingPreview = null;
  const cleanups = [];

  /** Пауза отрисовки, пока сцена не видна или вкладка скрыта — не тратим батарею. */
  function updatePause() {
    if (viewer) viewer.renderPaused = !visible || document.hidden;
  }

  async function loadTexture(source, model3d = "auto-detect") {
    try {
      await viewer.loadSkin(source, { model: model3d });
      return true;
    } catch (error) {
      return false;
    }
  }

  async function start3d(attempt = 0) {
    if (destroyed || !allow3d()) return;
    // Сцену могут вставить на страницу чуть позже (смена раздела через View Transition).
    if (!node.isConnected) {
      if (attempt < 20) setTimeout(() => start3d(attempt + 1), 100);
      return;
    }
    try {
      skinview = await loadSkinview();
    } catch (error) {
      return;
    }
    if (destroyed || !skinview) return;

    const width = model.clientWidth || 280;
    const height = model.clientHeight || 336;
    const canvas = el("canvas", { "aria-label": `3D-модель игрока ${name}`, role: "img" });
    try {
      viewer = new skinview.SkinViewer({
        canvas,
        width,
        height,
        pixelRatio: Math.min(window.devicePixelRatio || 1, 2),
        zoom: view.zoom ?? 0.7,
        fov: 38,
        animation: new skinview.IdleAnimation(),
      });
    } catch (error) {
      // WebGL недоступен — остаётся 2D-рендер.
      viewer = null;
      return;
    }
    viewer.controls.enableZoom = false;
    viewer.controls.enablePan = false;
    // Только горизонтальное вращение: персонаж не «заваливается» при перетаскивании.
    viewer.controls.minPolarAngle = Math.PI / 2;
    viewer.controls.maxPolarAngle = Math.PI / 2;
    // Поворот в три четверти — видно объём. Крутим обёртку: смена анимации
    // в skinview3d обнуляет поворот самой модели.
    viewer.playerWrapper.rotation.y = view.yaw ?? 0.45;
    viewer.playerWrapper.position.y = view.offsetY ?? 0;

    const loaded = pendingPreview
      ? await loadTexture(pendingPreview.url, pendingPreview.slim ? "slim" : "default")
      : await loadTexture(skinTextureUrl(current.hash, current.name))
        || (current.hash && await loadTexture(skinTextureUrl(null, current.name)));
    if (destroyed) {
      viewer.dispose();
      return;
    }
    if (!loaded) {
      viewer.dispose();
      viewer = null;
      return;
    }

    model.appendChild(canvas);
    node.classList.add("jk-stage--3d");
    if (hintNode) hintNode.classList.remove("opacity-0");

    const resize = new ResizeObserver(() => {
      if (viewer && model.clientWidth > 0) viewer.setSize(model.clientWidth, model.clientHeight);
    });
    resize.observe(model);
    const visibility = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      updatePause();
    });
    visibility.observe(node);
    document.addEventListener("visibilitychange", updatePause);
    cleanups.push(() => resize.disconnect(), () => visibility.disconnect(),
      () => document.removeEventListener("visibilitychange", updatePause));

    if (greet && autoStart3d()) wave();
  }

  let waveTimer = 0;

  /** Помахать рукой один раз, затем снова спокойное «дыхание». Новый взмах продлевает прежний. */
  function wave() {
    if (!viewer || !skinview) return;
    clearTimeout(waveTimer);
    viewer.animation = new skinview.WaveAnimation();
    waveTimer = setTimeout(() => {
      if (viewer) viewer.animation = new skinview.IdleAnimation();
    }, 2600);
  }

  function destroy() {
    destroyed = true;
    for (const cleanup of cleanups.splice(0)) cleanup();
    if (viewer) {
      viewer.dispose();
      viewer = null;
    }
  }
  onLeave(node, destroy);

  if (allow3d()) {
    if (autoStart3d()) {
      // На компьютере 3D грузится сам, когда страница уже показана и браузер свободен.
      whenIdle(() => start3d());
    } else if (touchLaunch) {
      // На телефоне — по кнопке: не тратим трафик и процессор, пока игрок не захотел.
      const launch = el("button", {
        type: "button",
        class: "jk-btn jk-btn--default jk-btn--sm absolute bottom-3 left-1/2 -translate-x-1/2 z-10 shadow-soft",
      }, [icon("rotate", 15), "Покрутить в 3D"]);
      launch.addEventListener("click", async () => {
        launch.disabled = true;
        launch.replaceChildren(el("span", { class: "jk-spinner", "aria-hidden": "true" }), "Загружаем 3D");
        await start3d();
        launch.remove();
        if (viewer) wave();
        else toast("3D на этом устройстве не запускается — показываем картинку", "info");
      });
      node.appendChild(launch);
    }
  }

  return {
    node,

    /** Сменить скин (например, пришёл свежий hash от игрового сервера). */
    setSkin(nextHash) {
      if (nextHash === current.hash) return;
      current = { hash: nextHash, name: current.name };
      if (!fallbackSrc) fallback.src = bodyRenderUrl(nextHash, current.name);
      if (viewer) loadTexture(skinTextureUrl(nextHash, current.name));
    },

    /** Помахать рукой (если 3D уже показано; в 2D ничего не происходит). */
    wave,

    /**
     * Показать скин из выбранного файла (object URL) — предпросмотр перед загрузкой.
     * slim — тонкие руки. Если 3D ещё грузится, файл покажется в нём, как только
     * загрузится. Возвращает true, только если файл уже виден в 3D; иначе нужен
     * 2D-предпросмотр развёртки.
     */
    async previewFile(url, slim) {
      pendingPreview = { url, slim };
      if (!viewer) return false;
      return loadTexture(url, slim ? "slim" : "default");
    },
  };
}
